import * as PIXI from 'pixi.js-legacy'
import { wait } from '../../../framework/src/helpers'
import DesktopUIScreen from '../../../framework/src/ui/desktop/DesktopUIScreen'
import AbstractUIScreen, {
	STAGE_MODES,
} from '../../../framework/src/ui/screen/AbstractUIScreen'
import { JokerWinCategory } from './JokerWinCategory'
import { JokerBonusHelpers } from './helpers'
import { IColorTheme } from './interfaces/IColorScheme'
import {
	IJackpotDefinition,
	IJackpotNotification,
	IJokerWinData,
} from './interfaces/IJackpotWin'
import eventsObservable from '../../../framework/src/events/EventsObservable'
export class JokerWinWrapper extends PIXI.Container {
	protected _categories: JokerWinCategory[][] = []
	public get categories(): JokerWinCategory[][] {
		return this._categories
	}
	protected maskGraphic: PIXI.Graphics
	public groupId: number
	protected readonly jackpotsOffset = 4
	paused: boolean
	animationDone: Promise<void>
	shouldFireDoneEvent: boolean
	animationCallback: () => void
	stepCount: number = 0
	protected bgHeight: number
	groupName: PIXI.Text
	updatePromise: any
	animationStepPromise: Promise<void[][]>
	maxJackpotLength: number
	blinking = false
	protected get maxJackpotsPerRow(): number {
		const screenData = this.screen.getScreenData()

		const splitSpacePredicate =
			this.screen instanceof DesktopUIScreen ||
			screenData.mode === STAGE_MODES.LAND

		return splitSpacePredicate ? 4 : 1
	}

	constructor(
		data: IJokerWinData,
		protected screen: AbstractUIScreen,
		bg: PIXI.Sprite,
		protected w: number,
		protected theme: IColorTheme,
		showGroupName: boolean,
		protected readonly animLength: number,
		protected decimalPlaces: number
	) {
		super()
		if (data.jackpots.length <= 0) {
			return
		}
		this.bgHeight = bg.height
		this.groupId = data.id
		this.groupName = new PIXI.Text(`  ${data.name.toUpperCase()}  `, {
			fontSize: this.bgHeight,
			lineHeight: this._height,
			fillGradientType: 0,
			dropShadowBlur: 3,
			dropShadowColor: 0x000000,
			dropShadowAngle: 1,
			dropShadowDistance: 4,
			dropShadow: true,
			fill: ['#006bcf', '#98dffa', '#1f94df', '#0438a3', '#1488c4'],
			fillGradientStops: [0.08, 0.35, 0.62, 0.8, 1],
			fontFamily: 'Audiowide-Regular',
			trim: true,
			miterLimit: 9,
			fontVariant: 'small-caps',
		})
		this.groupName.anchor.set(0, 0.5)
		this.groupName.position.set(10, 0)
		this.groupName.width = showGroupName ? 75 : 0
		this.groupName.scale.y = this.groupName.scale.x

		this.addChild(this.groupName)

		const longestJackpot = data.jackpots.reduce(
			(prev, next) => Math.max(prev, next.credit_value.toString().length),
			0
		)
		this.maxJackpotLength = longestJackpot
		const jackpotsSrc = data.jackpots
		jackpotsSrc.sort((a, b) => b.level - a.level)
		const groups: IJackpotDefinition[][] = []
		for (let i = 0; i < jackpotsSrc.length; i += this.maxJackpotsPerRow) {
			groups.push(jackpotsSrc.slice(i, i + this.maxJackpotsPerRow))
		}
		const bonusName = this.theme.setting.bonusName?.trim().toLowerCase()
		groups.forEach((group, j) => {
			const partWidth =
				(this.w - this.groupName.width - (showGroupName ? 2 * 10 : 0) - 6) /
				group.length
			this.categories.push(
				group.map((jackpot, index) => {
					const cat = new JokerWinCategory(
						jackpot.level_name,
						partWidth - this.jackpotsOffset,
						this.bgHeight - 4,
						jackpot.currency,
						{ ...this.theme.setting[jackpot.level_name], bonusName },
						jackpot.credit_value,
						longestJackpot,
						this.animLength,
						this.decimalPlaces
					)

					cat.x =
						this.groupName.x +
						this.groupName.width +
						(showGroupName ? 10 : -10) +
						(partWidth - this.jackpotsOffset) * index +
						this.jackpotsOffset * (index + 1)
					cat.y = -(cat.bg.height * cat.scale.y) / 2 + this.bgHeight * j

					this.addChild(cat)
					return cat
				})
			)
		})
		const mask = PIXI.Sprite.from(
			JokerBonusHelpers.solidFillTexture(this.w, this.bgHeight, '#ffffff')
		)
		this.mask = mask
		mask.isSprite = false
		mask.anchor.set(0, 0.5)
		this.addChild(mask)
	}

	get categoryNames(): string[] {
		const res: string[] = []
		for (const group of this.categories) {
			for (const cat of group) {
				res.push(cat.categoryName)
			}
		}
		return res
	}

	async onWinNotification(data: IJackpotNotification) {
		const catPredicate = (el: JokerWinCategory) =>
			el.categoryName === data.level_name
		const index = this.categories.findIndex((el) => el.some(catPredicate))

		if (index === -1) return
		this.blinking = true

		await this.scrollTo(index)
		eventsObservable.dispatch('joker_bonus-category-rotated')
		await this.categories[0].find(catPredicate)?.blink(data)
		this.blinking = false
		if (this.isScrollable) {
			this.animationStep()
		}
	}
	pause() {
		this.paused = true
	}
	resume() {
		this.paused = false
		if (this.shouldFireDoneEvent) {
			this.animationCallback?.()
		} else if (!(this.categories.length === 1)) {
			this.animationStep()
		}
	}

	destroy(options?: boolean | PIXI.IDestroyOptions): void {
		this.paused = true
		for (const group of this.categories) {
			for (const cat of group) {
				createjs.Tween.removeTweens(cat)
			}
		}
		this.groupName.destroy(true)
		super.destroy(options)
	}

	public async run() {
		this.animationDone = new Promise<void>((res) => {
			this.animationCallback = () => {
				res()
			}
		})

		this.shouldFireDoneEvent = false
		this.stepCount = 0
		this.paused = false

		if (!this.isScrollable) {
			wait(3000).then(() => this.onAnimationDone?.())
		} else {
			this.animationStep()
		}
		return this.animationDone
	}
	protected get isScrollable() {
		return !(this.categories.length === 1)
	}
	async scrollTo(index: number) {
		if (!this.isScrollable) {
			return
		}
		for (const group of this.categories) {
			for (const cat of group) {
				createjs.Tween.removeTweens(cat)
			}
		}

		const totalTime = 300
		if (this.useFade) {
			await Promise.all(
				this.categories.map((group, i) =>
					Promise.all(
						group.map(
							(cat, j) =>
								new Promise<void>((res) => {
									const currentAnimTime = 0
									const catTween = createjs.Tween.get(cat, {
										stopped: true,
										loop: false,
										override: true,
									})

									const fadeTime = totalTime
									catTween.to(
										{
											y: -(cat.bg.height * cat.scale.y) / 2,
										},
										0
									)
									if (i === index) {
										catTween.wait(fadeTime * (1 / 2))
									}
									//tur off interactivity of all categories
									catTween.call(() => {
										cat.interactive = false
									})
									catTween.to(
										{
											alpha: i === index ? 1 : 0,
										},
										fadeTime,
										createjs.Ease.easeOut
									)
									if (i < index) {
										catTween.call(() => {
											this.setChildIndex(cat, this.children.length - 2)
										})
									}
									//turn on interactivity for currently displayed category
									catTween.call(() => {
										cat.interactive = i === index
									})

									catTween.call(res)
									catTween.gotoAndPlay(currentAnimTime)
								})
						)
					).then((res) => {
						if (i < index) this.categories.push(this.categories.shift())
						return res
					})
				)
			)
			return
		}
		const stepTime = totalTime / index + 1
		for (let i = 0; i < index; i++) {
			await Promise.all(
				this.categories.map((group, index) =>
					Promise.all(
						group.map(
							(cat, j) =>
								new Promise<void>((res) => {
									const currentAnimTime = 0
									const catTween = createjs.Tween.get(cat, {
										stopped: true,
										loop: false,
										override: true,
									})
									catTween.to(
										{
											y:
												-(cat.bg.height * cat.scale.y) / 2 +
												this.bgHeight * (index - 1),
										},
										stepTime
									)
									if (index === 0) {
										catTween
											.to(
												{
													y:
														-(cat.bg.height * cat.scale.y) / 2 +
														this.bgHeight * (this.categories.length - 1),
												},
												0
											)
											.call(() => {
												this.setChildIndex(cat, this.children.length - 2)
												this.categories.push(this.categories.shift())
											})
									}
									catTween.call(res)
									catTween.gotoAndPlay(currentAnimTime)
								})
						)
					)
				)
			)
		}
	}
	protected get useFade() {
		const screenData = this.screen.getScreenData()
		return (
			this.screen instanceof DesktopUIScreen ||
			screenData.mode === STAGE_MODES.LAND
		)
	}
	protected async animationStep() {
		await wait(3000)
		if (this.paused) {
			return
		}
		if (this.blinking) {
			return
		}
		this.stepCount++
		if (this.stepCount > this.categories.length) {
			this.onAnimationDone?.()
			return
		}

		for (const group of this.categories) {
			for (const cat of group) {
				createjs.Tween.removeTweens(cat)
			}
		}
		this.animationStepPromise = Promise.all(
			this.categories.map((group, index) =>
				Promise.all(
					group.map(
						(cat, j) =>
							new Promise<void>((res) => {
								const currentAnimTime = 0
								const catTween = createjs.Tween.get(cat, {
									stopped: true,
									loop: false,
									override: true,
								})

								if (this.useFade) {
									const fadeTime = 1000
									catTween.to(
										{
											y: -(cat.bg.height * cat.scale.y) / 2,
										},
										0
									)
									if (index === 1) {
										catTween.wait(fadeTime * (1 / 2))
									}
									//tur off interactivity of all categories
									catTween.call(() => {
										cat.interactive = false
									})
									catTween.to(
										{
											alpha: index === 1 ? 1 : 0,
										},
										fadeTime,
										createjs.Ease.easeOut
									)
									if (index === 0) {
										catTween.call(() => {
											this.setChildIndex(cat, this.children.length - 2)
										})
									}
									//turn on interactivity for currently displayed category
									catTween.call(() => {
										cat.interactive = index === 1
									})
								} else {
									catTween.to(
										{
											y:
												-(cat.bg.height * cat.scale.y) / 2 +
												this.bgHeight * (index - 1),
										},
										1000
									)
									if (index === 0) {
										catTween
											.to(
												{
													y:
														-(cat.bg.height * cat.scale.y) / 2 +
														this.bgHeight * (this.categories.length - 1),
												},
												0
											)
											.call(() => {
												this.setChildIndex(cat, this.children.length - 2)
											})
									}
								}

								catTween.call(res)
								catTween.gotoAndPlay(currentAnimTime)
							})
					)
				).then((res) => {
					if (index === 0) this.categories.push(this.categories.shift())
					return res
				})
			)
		)
		//.then(() => this.animationStep());
		await this.animationStepPromise
		this.animationStep()
	}

	onAnimationDone = () => {
		if (this.paused) {
			this.shouldFireDoneEvent = true
			return
		}

		this.animationCallback?.()
	}

	getMaskedHeight() {
		return (this.children[0] as PIXI.Sprite).height
	}

	async update(data: IJokerWinData) {
		if (data.jackpots.length < 0) {
			return
		}

		const longestJackpot = data.jackpots.reduce(
			(prev, next) => Math.max(prev, next.credit_value.toString().length),
			0
		)
		if (longestJackpot > this.maxJackpotLength) {
			this.maxJackpotLength = longestJackpot
			this.categories.forEach((group) => {
				group.forEach((cat) => cat.setDigitsCount(longestJackpot))
			})
		}
		this.updatePromise = Promise.all(
			data.jackpots.map((jackpot) => {
				return this.categories
					.find((group) =>
						group.some((cat) => jackpot.level_name === cat.categoryName)
					)
					.find((cat) => jackpot.level_name === cat.categoryName)
					.setNewValue(jackpot.credit_value)
			})
		)
		await this.updatePromise

		if (longestJackpot < this.maxJackpotLength) {
			this.maxJackpotLength = longestJackpot
			this.categories.forEach((group) => {
				group.forEach((cat) => cat.setDigitsCount(longestJackpot))
			})
		}
	}
}
